import { Provider } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';

import { MethodTypeEditorContext } from '../../../effects/method-type-editor.effects';
import type { MethodPipelineContext } from '../../../state/assigned-pipelines.state';
import { METHOD_PIPELINE_MANAGER_CONTEXT } from '../../pipelines/method-pipeline-manager/method-pipeline-manager.facade';
import { methodPipelineManagerProviders } from '../../pipelines/method-pipeline-manager/method-pipeline-manager.facade.providers';
import { pipelineEditorCascadeProviders } from '../../pipelines/pipeline-editor/pipeline-editor.facade.providers';
import { RESOURCE_MANAGER_CONTAINER_ID } from '../../resources/resource-manager/resource-manager.facade';
import { resourceManagerProviders } from '../../resources/resource-manager/resource-manager.facade.providers';

import { METHOD_TYPE_EDITOR_CONTEXT } from './method-type-editor-page.facade';
import { methodTypeEditorProviders } from './method-type-editor.facade.providers';

/**
 * The full DI scope of the method-type editor page — the first consumer that
 * composes the editor trio, BOTH smart managers and the page-level save
 * cascade in one injector. The page is the outermost consumer of every shared
 * session ledger, so all of them resolve here and nothing below shadows them.
 */
export const methodTypeEditorPageProviders: Provider[] = [
  // (1) Session identity — resolved once at first injection (component-level
  // factories run lazily at/after construction, when the route snapshot is
  // populated). A new method type mints ONE tmp- placeholder here so the same
  // id feeds the pipeline-manager context, the resource container id, the
  // resource-tree root, and the consolidateMethodType tmp→real id remap.
  {
    provide: METHOD_TYPE_EDITOR_CONTEXT,
    useFactory: (route: ActivatedRoute): MethodTypeEditorContext => {
      const routeId = route.snapshot.paramMap.get('methodTypeId'); // null on the create route
      return routeId !== null
        ? { methodTypeId: routeId, persisted: true }
        : { methodTypeId: `tmp-${uuidv4()}`, persisted: false };
    },
    deps: [ActivatedRoute],
  },

  // (2) The editor trio + the CONTINUOUS asset-type catalog + retrieve query.
  ...methodTypeEditorProviders,

  // (3) Pipeline manager: catalog + assigned lists + the editor-dialog SESSION
  // tier (the cascade tier arrives in (5)).
  ...methodPipelineManagerProviders,
  {
    provide: METHOD_PIPELINE_MANAGER_CONTEXT,
    useFactory: (context: MethodTypeEditorContext): MethodPipelineContext => ({
      methodTypeId: context.methodTypeId,
      persisted: context.persisted,
    }),
    deps: [METHOD_TYPE_EDITOR_CONTEXT],
  },

  // (4) Resource manager. RESOURCE_MANAGER_NEW_RESOURCE_TYPE_LOCK is
  // deliberately NOT provided — the method-type editor keeps a free type
  // select (the lock is the resource editor's mask-session concern).
  ...resourceManagerProviders,
  {
    provide: RESOURCE_MANAGER_CONTAINER_ID,
    useFactory: (context: MethodTypeEditorContext): string => context.methodTypeId,
    deps: [METHOD_TYPE_EDITOR_CONTEXT],
  },

  // (5) Page cascade + label ledgers. This spread ALSO supplies every shared
  // staging ledger the managers rely on — the resource pair
  // (StagedResourcesState / AssignedResourcesState) AND the pipeline pair
  // (StagedPipelinesState / AssignedPipelinesState) — plus the
  // MethodTypeEditorState row the cascade injects (deduped with (2)'s by the
  // flattened injector). The manager bundles deliberately exclude all of them.
  ...pipelineEditorCascadeProviders,
];
